import React from "react";
import ReactDOMServer from "react-dom/server";
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import { getSVGImage, getImageData } from "./utils";
import { isEqual } from "lodash/lang";
import { withMapContext } from "../MapContext";

class MarkerLayer extends React.Component {
  constructor(props) {
    super(props);
    this.markerSource = "markerSource_" + this.props.mapId; 
    this.markerLayer = "markerLayer_" + this.props.mapId;
    this.markerTooltip = "markerTooltip_" + this.props.mapId;
    this.markerImages = []; // Tracks list of images created & added to map
    this.trimbleMarkers = []; // Tracks all marker object references (for non svg images)- to delete on unmoubnt
  }

  componentDidMount() {
    if (this.props.isStyleLoaded && this.props.data.length) {
      this.renderMarkerLayer();
    }
  }

  componentDidUpdate(prevProps) {
    // replace source data if active markers changed or actual marker list changes
    // this will rerender markers without needing to add or delete layers
  
    const layerExists = Boolean(this.props.map.getLayer(this.markerLayer));
    if (layerExists && (!isEqual(prevProps.activeMarkerIds, this.props.activeMarkerIds)  || !isEqual(prevProps.data, this.props.data) )) {
      this.replaceMakerSourceData(this.props.data);
    } else if (
      this.props.isStyleLoaded &&
      this.props.data.length &&
      !layerExists
    ) {
      this.renderMarkerLayer();
    }
    // Focus on active center
    if (this.props.center && (!isEqual(this.props.center, prevProps.center) || this.props.applyOffset !== prevProps.applyOffset)) {
      this.props.easeToAnimation
      ? map.easeTo({
          center: this.props.position,
          zoom: this.props.mapZoomLevel
      }) :
      this.props.map.flyTo({
        center: this.props.center,
        offset: this.props.centerOffset || [0, 0],
      });
    }
  }

  componentWillUnmount() {
    this.props.map.off("click", this.markerLayer, this.markerLayerClicked);
    this.props.map.off("mousemove", this.markerLayer, this.markerLayerMouseMoved);
    this.props.map.off("mouseleave", this.markerLayer, this.markerLayerMouseLeave);
    this.removeTrimbleMarkers();
    this.removeMarkerLayer();
  }

  removeMarkerLayer = () => {
    if (this.props.map._removed) {
      return;
    }
    if (this.props.map.getLayer(this.markerSource)) {
      this.props.map.removeLayer(this.markerSource);
    }
    if (this.props.map.getSource(this.markerLayer)) {
      this.props.map.removeSource(this.markerLayer);
    }
  };

  addIconImageToMap = (markerData) => {
    const markerIcon = this.props.getIcon(markerData);
    const string = ReactDOMServer.renderToString(markerIcon);
    const detail = markerIcon.props.children.props;
    let markerProperties = null;

    // If a marker is of type svg & non active , then render them as images
    if (string.includes("<svg") && !this.props.activeMarkerIds.includes(markerData[this.props.dataKey])) {
      const markerName =detail["data-markername"];
        markerProperties = {
          id: markerData[this.props.dataKey],
          icon: markerName,          // tooltipData: detail["data-tooltip"],
          tooltipHTML: this.props.showTooltip ?
            JSON.parse(detail["data-tooltip"])?.filter(Boolean).join("<br/>") : undefined,
          markerLabel: this.props.getMarkerLabel?.(markerData)
        };
      if (
        !this.markerImages.includes(markerName) &&
        !this.props.map.hasImage(markerName)
      ) {
        this.markerImages.push(markerName);
        getSVGImage(string)
          .then(getImageData)
          .then((imageData) => {
            if (imageData) {
              this.props.map.addImage(markerName, imageData);
            }
          });
      }
    } else {
      // If a marker is not of type svg or is active (to show this icon on top of all other icons) , then render them as images
      this.addTrimbleMarker(markerData, markerIcon);
    }

    return markerProperties;
  };

  addTrimbleMarker = (markerData, markerIcon) => {
    // Create a trimble marker by creating a local div & rendering the icons into it
    const markerContainer = document.createElement("div");
    markerContainer.id = "marker_" + markerData[this.props.dataKey];
    markerContainer.style = "cursor:pointer";
    markerContainer.onmousemove = this.markerMouseMoved;
    markerContainer.onclick = (event) => {
      event.stopPropagation();
      this.markerClicked(markerData[this.props.dataKey]);
    };
    ReactDOM.render(markerIcon, markerContainer);
    const marker = new TrimbleMaps.Marker({
      element: markerContainer,
    })
      .setLngLat({ lat: markerData.latitude, lng: markerData.longitude })
      .addTo(this.props.map);
    this.trimbleMarkers.push(marker);
  };

  markerMouseMoved = (event) => {
    // prevents both marker & symbol tooltip appearing when they overlap
    event.stopPropagation();
  }

  renderMarkerLayer = () => {
    if (!this.props.map) {
      return;
    }

    this.markerImages = [];
    this.addMarkerSource(this.props.data);
    // Add a layer to draw symbols for each point in the data source. Use an expression to choose a data-driven icon.
    const { layout, paint } = this.props.markerLabelProps || {}
    this.props.map.addLayer({
      id: this.markerLayer,
      source: this.markerSource,
      type: "symbol",
      layout: {
        "icon-image": ["get", "icon"], // reads icon  from properties of source data
        // get the marker label from the source's "markerLabel" property and show label only when map zoom level >= threshold zoom level 
        "text-field": ["step", ["zoom"], "", this.props.thresholdZoomLevel ?? 9, ["get", "markerLabel"]],
        "icon-rotation-alignment": "map",
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "text-font": ["Roboto Regular"],
        ...layout
      },
      paint: {
        ...paint
      }
    }); 
    this.props.map.on("click", this.markerLayer, this.markerLayerClicked);
    this.props.map.on("mousemove", this.markerLayer, this.markerLayerMouseMoved);
    this.props.map.on("mouseleave", this.markerLayer, this.markerLayerMouseLeave);
  };

  
  addMarkerSource = (data) => {
    const markerSourceData = {
      type: "FeatureCollection",
      features: data
        .map((markerData) => {
          const markerProperties = this.addIconImageToMap(markerData);
          if (!markerProperties) {
            return null; // skip active & non svg markers as we create trimble map markers for them
          }
          return {
            type: "Feature",
            properties:markerProperties,
            geometry: {
              type: "Point",
              coordinates: [markerData.longitude, markerData.latitude],
            },
          };
        })
        .filter(Boolean),
    };
    const markerSource = this.props.map.getSource(this.markerSource);

    // Update the data source when already exists,
    // this happens on active marker list change or terminal change
    if (markerSource) {
      markerSource.setData(markerSourceData);
    } else {
      const markerGeojson = {
        type: "geojson",
        data: markerSourceData,
      };
      this.props.map.addSource(this.markerSource, markerGeojson);
    }
  };

  replaceMakerSourceData = () => {
    this.removeTrimbleMarkers();
    this.addMarkerSource(this.props.data);
  };

  removeTrimbleMarkers = () => {
    this.trimbleMarkers.map((marker) => marker.remove());
    this.trimbleMarkers = [];
  };

  markerLayerClicked = (event) => {
    const id = event.features.find(
      (feature) =>
        feature.layer.id === this.markerLayer && feature.layer.type === "symbol"
    )?.properties?.id;
    this.markerClicked(id);
  };

  markerClicked = (id) => {
    const markerData = this.props.data.find(
      (marker) => marker[this.props.dataKey] === id
    );
    if (markerData) {
      this.props.onClick(markerData);
    }
  };

  markerLayerMouseMoved = (event) => {
    this.props.map.getCanvas().style.cursor = "pointer";
    if (this.props.showTooltip) {
      event.features.length ? this.showTooltip(event) : this.hideTooltip();
    }
  };

  markerLayerMouseLeave = () => {
    this.props.map.getCanvas().style.cursor = "grab";
    this.hideTooltip();
  };

  showTooltip = (event) => {
   
    const properties = event.features.find(
      (feature) =>
        feature.layer.id === this.markerLayer && feature.layer.type === "symbol"
    )?.properties;

    if (!properties.tooltipHTML) {
      return
    }

    let tooltip = document.getElementById(this.markerTooltip);
    if(tooltip){
        if(tooltip.getAttribute("data-markerTooltipId") === properties.id){
          return;
        }
         tooltip.remove();
    }

    // Tooltip is shown here by creating a div element as a child of document.body & attach cursor position as absolute position of div

    tooltip = document.createElement("div");
    tooltip.id = this.markerTooltip;
    tooltip.className ="markerLayerTooltip"
      
    tooltip.setAttribute("data-markerTooltipId", properties.id);
    tooltip.style.position ="absolute";
    tooltip.style.transform = `translate3d(${event.point.x + "px"},${event.point.y + 10 + "px"},0px)`    
    tooltip.style.willChange ="transform"; 
    tooltip.style.zIndex = 1500;
    tooltip.innerHTML = properties.tooltipHTML;
    document.getElementById(this.props.mapId).appendChild(tooltip)
  }

  hideTooltip = () => {
     document.getElementById(this.markerTooltip)?.remove();
  }

  render() {
    return null;
  }
}

export default withMapContext(MarkerLayer)